import { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Real-time notifications via Server-Sent Events.
 *
 * Why SSE?
 * --------
 * Replaces the email-only notification path with instant in-app delivery
 * (user request 2026-05-03 "I hate that we use email and not real time
 * notifications"). SSE works everywhere without VAPID keys, domain
 * verification, or iOS PWA install — when the app is open, new DMs/blasts/
 * checkpoint alerts appear as toasts within ~100ms of the server firing
 * them.
 *
 * Fallback ladder (user-chosen 3c):
 *   1. SSE delivers → toast + `onEvent` callback (client fires
 *      POST /notifications/ack to suppress the email fallback).
 *   2. If no SSE ack within 5s, the backend sends the email.
 *   3. Bell always receives the event regardless of SSE state.
 *
 * Reconnection: `EventSource` auto-reconnects on network drops. We still
 * bind `error` just to log — don't need to manually re-open.
 */
export function useRealtimeNotifications({ crewId, onEvent }) {
  const esRef = useRef(null);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!crewId) return undefined;
    const url = `${API}/notifications/stream?crew_id=${encodeURIComponent(crewId)}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.onmessage = (raw) => {
      let evt;
      try { evt = JSON.parse(raw.data); } catch { return; }
      if (!evt || evt.kind === "ready") return;

      // Fire the user-supplied handler first so pages can update unread
      // counts / inbox lists before the toast shows.
      try { onEventRef.current?.(evt); } catch (e) { console.error("onEvent handler threw:", e); }

      // Surface a toast for anything that isn't a silent server-origin
      // signal (e.g., bell-refresh pings). `kind` is set by the publisher.
      // We rely on the publisher to set a human-readable `title` — if
      // nothing's provided, we skip the toast entirely rather than show
      // empty text.
      if (evt.title) {
        const t = toast.message(evt.title, {
          description: evt.body || undefined,
          action: evt.route
            ? {
                label: "Open",
                onClick: () => {
                  // The route string is an app-relative hash or query param
                  // understood by App.js. Examples: "inbox", "manager",
                  // "crew-tally".
                  try { window.location.search = `?route=${encodeURIComponent(evt.route)}`; }
                  catch { /* ignore */ }
                },
              }
            : undefined,
          duration: 6000,
        });
        void t;
      }

      // Ack the event so the backend skips the 5s email fallback. Best-
      // effort — if this POST fails the user still got the toast, they
      // just might also get an email. No-op in that case.
      if (evt.id) {
        axios
          .post(`${API}/notifications/ack`, { crew_id: crewId, event_id: evt.id })
          .catch(() => { /* swallow — delivery already succeeded */ });
      }
    };

    es.onerror = (e) => {
      // EventSource auto-reconnects by default; we just log so the user's
      // console shows when a reconnect is in progress.
      // eslint-disable-next-line no-console
      console.debug("SSE connection blip, will auto-reconnect:", e?.type);
    };

    return () => {
      try { es.close(); } catch { /* ignore */ }
      esRef.current = null;
    };
  }, [crewId]);
}
